// src/hooks/useCurrentProvider.ts
import { useState, useEffect, useContext } from "react";
import { SERVER_URL } from "../config";
import { AuthContext } from "../context/AuthContext";

export interface User {
  id: string;
  name: string;
  email: string;
  role: "PROVIDER" | "CLINIC_MANAGER" | "ADMIN" | "PATIENT";
}

interface Result {
  user: User | null;
  isProvider: boolean;
  isClinic: boolean;
  isAdmin: boolean;
  isPatient: boolean;
  loading: boolean;
  error: string | null;
}

const useCurrentProvider = (): Result => {
  const { token } = useContext(AuthContext); // ← observe token
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(!!token);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // token removed → clear user
    if (!token) {
      setUser(null);
      localStorage.removeItem("currentUser");
      setLoading(false);

      return;
    }

    // try cache tied to current token
    const cacheKey = `currentUser:${token}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      setUser(JSON.parse(cached) as User);
      setLoading(false);
      return;
    }

    // fetch fresh user for new token
    const fetchUser = async () => {
      try {
        const res = await fetch(`${SERVER_URL}/api/auth/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to fetch user");
        const data: User = await res.json();
        setUser(data);
        localStorage.setItem(cacheKey, JSON.stringify(data));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setLoading(false);
      }
    };

    setLoading(true);
    fetchUser();
  }, [token]); // ← refetch on change

  return {
    user,
    isProvider: user?.role === "PROVIDER",
    isClinic: user?.role === "CLINIC_MANAGER",
    isAdmin: user?.role === "ADMIN",
    isPatient: user?.role === "PATIENT",
    loading,
    error,
  };
};

export default useCurrentProvider;
